import React from 'react';
import {CssImageTheme, ImageCreate, ThemeCreate} from '../../../../../type/cssTemplate';
import {ThemeInfo} from './types';

type ThemeCreatePreview = Omit<ThemeCreate, 'images'> & {
    images: Array<ImageCreate & { file?: { fileFullUrl?: string } }>;
};

interface TemplateThemeListProps {
    role?: string;
    availableThemes: ThemeInfo[];
    onLoadTheme: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    onRequestNewThemeInput: () => void;
    showNewThemeInput: boolean;
    newThemeName: string;
    onChangeNewThemeName: (value: string) => void;
    onSubmitNewTheme: () => void;
    onCancelNewTheme: () => void;
    themes: CssImageTheme[];
    newThemes: ThemeCreatePreview[];
    selectedMainTheme: string | null;
    selectedNewTheme: string | null;
    onSelectMainTheme: (themeName: string) => void;
    onOpenTheme: (themeName: string) => void;
    onOpenNewTheme: (themeName: string) => void;
    uploadedImages: Map<string, string>;
    isDefaultTheme: (theme: CssImageTheme) => boolean;
}

const TemplateThemeList: React.FC<TemplateThemeListProps> = ({
    role,
    availableThemes,
    onLoadTheme,
    onRequestNewThemeInput,
    showNewThemeInput,
    newThemeName,
    onChangeNewThemeName,
    onSubmitNewTheme,
    onCancelNewTheme,
    themes,
    newThemes,
    selectedMainTheme,
    selectedNewTheme,
    onSelectMainTheme,
    onOpenTheme,
    onOpenNewTheme,
    uploadedImages,
    isDefaultTheme,
}) => {
    const getPreviewGridCols = (length: number) => {
        if (length === 1) return 'grid-cols-1';
        if (length === 2) return 'grid-cols-2';
        if (length === 3) return 'grid-cols-3';
        if (length === 4) return 'grid-cols-2';
        return 'grid-cols-3';
    };

    const getPreviewHeightClass = (length: number) => (length <= 2 ? 'h-[80px]' : 'h-[50px]');

    const renderPreviewImages = (theme: CssImageTheme) => {
        const previewImages = theme.images.slice(0, 5);
        const gridColsClass = getPreviewGridCols(previewImages.length);
        const itemHeightClass = getPreviewHeightClass(previewImages.length);

        return (
            <div className={`grid flex-1 gap-1 ${gridColsClass}`}>
                {previewImages.map(image => (
                    <div
                        key={image.id}
                        className={`w-full overflow-hidden rounded-md border border-gray-200 ${itemHeightClass}`}
                    >
                        <img
                            src={uploadedImages.get(image.cssName) || image.file.fileFullUrl}
                            alt={image.cssName}
                            loading="lazy"
                            className="h-full w-full object-cover"
                        />
                    </div>
                ))}
                {theme.images.length > 5 && (
                    <div
                        className={`flex items-center justify-center rounded-md bg-gray-100 text-xs font-medium text-gray-500 ${itemHeightClass}`}
                    >
                        +{theme.images.length - 5}
                    </div>
                )}
            </div>
        );
    };

    const renderNewThemePreview = (theme: ThemeCreatePreview) => {
        const previewImages = theme.images.slice(0, 5);
        if (previewImages.length === 0) {
            return (
                <div className="flex h-[80px] items-center justify-center rounded-md bg-gray-100 text-xs text-gray-400">
                    이미지 없음
                </div>
            );
        }

        return (
            <div className="grid flex-1 grid-cols-3 gap-1">
                {previewImages.map(image => (
                    <div
                        key={image.cssName}
                        className="h-[60px] w-full overflow-hidden rounded-md border border-gray-200"
                    >
                        <img
                            src={image.file?.fileFullUrl}
                            alt={image.cssName}
                            loading="lazy"
                            className="h-full w-full object-cover"
                        />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="template-modal-filters flex-1 overflow-auto px-6 py-5">
            <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-700">테마 선택</h3>
                <div className="flex items-center gap-2">
                    {(role === 'ROLE_USER' || role === 'ROLE_MARKETING') && availableThemes.length > 0 && (
                        <select
                            onChange={onLoadTheme}
                            defaultValue=""
                            className="max-w-[200px] rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                        >
                            <option value="" disabled>다른 상품의 테마 불러오기</option>
                            {availableThemes.map(theme => (
                                <option key={theme.productId} value={theme.productId}>
                                    {theme.productName}
                                </option>
                            ))}
                        </select>
                    )}
                    {role === 'ROLE_ADMIN' && (
                        <button
                            type="button"
                            onClick={onRequestNewThemeInput}
                            className="rounded-md bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-600"
                        >
                            + 테마 추가
                        </button>
                    )}
                </div>
            </div>

            {showNewThemeInput && (
                <div className="mb-4 rounded-lg bg-sky-50 p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                            type="text"
                            placeholder="새 테마 이름"
                            value={newThemeName}
                            onChange={(event) => onChangeNewThemeName(event.target.value)}
                            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
                        />
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={onSubmitNewTheme}
                                className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600"
                            >
                                추가
                            </button>
                            <button
                                type="button"
                                onClick={onCancelNewTheme}
                                className="rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-600"
                            >
                                취소
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="template-category-grid">
                {themes.map(theme => {
                    const isMainSelected = selectedMainTheme === theme.themeName;
                    return (
                        <div
                            key={theme.themeName}
                            className={`template-category-item ${isMainSelected ? 'selected' : ''}`}
                        >
                            <div className="mb-3 flex w-full items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-700">
                                        {theme.themeName}
                                    </span>
                                </div>
                                {isMainSelected && (
                                    <span className="text-xs font-semibold text-indigo-500">
                                        현재 사용중인 테마입니다.
                                    </span>
                                )}
                                <input
                                    type="radio"
                                    name="mainCategorySelection"
                                    checked={isMainSelected}
                                    onChange={(event) => {
                                        event.stopPropagation();
                                        onSelectMainTheme(theme.themeName);
                                    }}
                                    title="메인 테마로 설정"
                                    className="h-4 w-4 cursor-pointer"
                                />
                            </div>

                            {renderPreviewImages(theme)}

                            {!isDefaultTheme(theme) && (
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onOpenTheme(theme.themeName);
                                    }}
                                    className="mt-2 rounded-md border border-gray-300 bg-gray-100 px-3 py-1.5 text-xs text-slate-700 transition-colors hover:bg-gray-200"
                                >
                                    상세보기
                                </button>
                            )}
                        </div>
                    );
                })}

                {newThemes.map(theme => {
                    const isSelected = selectedNewTheme === theme.themeName;
                    return (
                        <div
                            key={theme.themeName}
                            className={`template-category-item border-2 border-dashed border-blue-500 ${isSelected ? 'selected' : ''}`}
                        >
                            <div className="mb-3 flex w-full items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-700">
                                        {theme.themeName}
                                    </span>
                                    <span className="rounded bg-blue-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                                        신규
                                    </span>
                                </div>
                                <input
                                    type="radio"
                                    name="mainThemeSelection"
                                    checked={selectedMainTheme === theme.themeName}
                                    onChange={(event) => {
                                        event.stopPropagation();
                                        onSelectMainTheme(theme.themeName);
                                    }}
                                    title="메인 테마로 설정"
                                    className="h-4 w-4 cursor-pointer"
                                />
                            </div>

                            {renderNewThemePreview(theme)}

                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onOpenNewTheme(theme.themeName);
                                }}
                                className="mt-2 w-full rounded-md bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-600"
                            >
                                이미지 추가
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TemplateThemeList;
